// The OEM administrator's application (chosen in App.tsx from /api/auth/me): a deliberately
// separate, read-only shell. It renders none of the platform or operational UI (no SOS banner,
// connection badge, radios, map, dispatch or Settings) and loads data ONLY from /api/oem/*.
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IdentityBadge } from '../../components/Identity';
import AccountSecurityPage from '../account/AccountSecurity';
import { useIdentity } from '../../services/identity';
import {
  OEMApiError,
  getOEMCustomer,
  getOEMCustomers,
  getOEMDepartments,
  getOEMNetworks,
  getOEMSelf,
  getOEMUsers,
  oemErrorMessage,
  type OEMCustomerView,
  type OEMSelf,
} from '../../services/oem';
import { resolveOEMRoute } from './oemModel';
import { OEMCustomerDetail, OEMCustomerList, OEMNotAvailable, OEMOverview, OEMShell, StatePanel, type CustomerDetailData, type PanelState } from './OEMViews';

const errorState = (err: unknown): PanelState => ({
  kind: 'error',
  text: err instanceof OEMApiError ? err.message : oemErrorMessage('unavailable'),
});

export default function OEMApp() {
  const { me } = useIdentity();
  const location = useLocation();
  const route = resolveOEMRoute(location.pathname);
  return (
    <OEMShell oemName={me?.oem_partner?.name || 'OEM Partner'} active={route.kind === 'home' ? '/OEM' : ''} badge={<IdentityBadge />}>
      {route.kind === 'home' && <OEMHome />}
      {route.kind === 'customer' && <OEMCustomerPage key={route.id} id={route.id} tab={route.tab} />}
      {route.kind === 'account' && <AccountSecurityPage />}
      {route.kind === 'not-available' && <OEMNotAvailable />}
    </OEMShell>
  );
}

function OEMHome() {
  const [self, setSelf] = useState<OEMSelf | null>(null);
  const [selfState, setSelfState] = useState<PanelState | undefined>({ kind: 'loading' });
  const [customers, setCustomers] = useState<OEMCustomerView[]>([]);
  const [customersState, setCustomersState] = useState<PanelState | undefined>({ kind: 'loading' });
  useEffect(() => {
    getOEMSelf().then((s) => { setSelf(s); setSelfState(undefined); }).catch((e) => setSelfState(errorState(e)));
    getOEMCustomers().then((c) => { setCustomers(c); setCustomersState(undefined); }).catch((e) => setCustomersState(errorState(e)));
  }, []);
  return (
    <>
      {self ? <OEMOverview self={self} /> : selfState && <StatePanel state={selfState} />}
      <OEMCustomerList customers={customers} state={customersState} />
    </>
  );
}

function OEMCustomerPage({ id, tab }: { id: string; tab: 'networks' | 'departments' | 'users' }) {
  const [customer, setCustomer] = useState<OEMCustomerView | null>(null);
  const [pageState, setPageState] = useState<PanelState | undefined>({ kind: 'loading' });
  const [data, setData] = useState<CustomerDetailData>({});
  const [tabState, setTabState] = useState<PanelState | undefined>({ kind: 'loading' });

  useEffect(() => {
    getOEMCustomer(id).then((c) => { setCustomer(c); setPageState(undefined); }).catch((e) => setPageState(errorState(e)));
  }, [id]);

  useEffect(() => {
    if (!customer) return;
    setTabState({ kind: 'loading' });
    // Every request re-checks the server: a customer moved to another OEM becomes unavailable.
    const load = async (): Promise<CustomerDetailData> => {
      if (tab === 'networks') return { networks: await getOEMNetworks(id) };
      if (tab === 'departments') return { departments: await getOEMDepartments(id) };
      const [users, networks] = await Promise.all([getOEMUsers(id), getOEMNetworks(id)]);
      return { users, networks };
    };
    load().then((d) => { setData(d); setTabState(undefined); }).catch((e) => setTabState(errorState(e)));
  }, [customer, id, tab]);

  if (!customer) return <StatePanel state={pageState || { kind: 'loading' }} />;
  return <OEMCustomerDetail customer={customer} tab={tab} data={data} state={tabState} />;
}
